import { useState, useEffect } from "react"
import { useNavigate } from "@tanstack/react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { fetcher } from "../lib/api"
import { getPlan } from "../lib/plans"
import { GRADIENTS, parseLinearGradient, buildLinearGradient } from "../lib/colors"
import { Button, Input, Label, Dialog, DialogTitle, Textarea } from "./ui"
import { ColorPicker } from "./color-picker"
import { useToast } from "./toast"
import { Copy } from "lucide-react"

/** Board settings dialog. Lets you rename the board, pick a color gradient, duplicate, or delete it. Color selection is gated by plan. */
export function BoardSettingsDialog({
  open,
  onClose,
  boardId,
  workspaceId,
}: {
  open: boolean
  onClose: () => void
  boardId: string
  workspaceId: string
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const navigate = useNavigate()

  const [editName, setEditName] = useState("")
  const [editDescription, setEditDescription] = useState("")
  const [editColor, setEditColor] = useState("")
  const [gradientFrom, setGradientFrom] = useState("#667eea")
  const [gradientTo, setGradientTo] = useState("#764ba2")
  const [gradientAngle, setGradientAngle] = useState(135)

  const { data: board } = useQuery({
    queryKey: ["board", boardId],
    queryFn: () => fetcher<{ name: string; description: string | null; color: string | null; projectId: string }>(`/api/boards/${boardId}`),
    enabled: open && !!boardId,
  })

  const { data: workspace } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetcher<{ plan: string }>(`/api/workspaces/${workspaceId}`),
    enabled: open && !!workspaceId,
  })

  useEffect(() => {
    if (board && open) {
      setEditName(board.name)
      setEditDescription(board.description || "")
      setEditColor(board.color || "")
      const parsed = board.color && parseLinearGradient(board.color)
      if (parsed) {
        setGradientFrom(parsed.from)
        setGradientTo(parsed.to)
        setGradientAngle(parsed.angle)
      }
    }
  }, [board, open])

  const updateBoard = useMutation({
    mutationFn: (body: { name: string; description?: string; color?: string | null }) =>
      fetcher(`/api/boards/${boardId}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] })
      queryClient.invalidateQueries({ queryKey: ["project"] })
      toast("Board updated", "success")
      onClose()
    },
    onError: (err: any) => toast(err.message || "Failed to update board", "error"),
  })

  const deleteBoard = useMutation({
    mutationFn: () => fetcher(`/api/boards/${boardId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project"] })
      toast("Board deleted", "success")
      onClose()
      navigate({ to: "/" })
    },
    onError: (err: any) => toast(err.message || "Failed to delete board", "error"),
  })

  const duplicateBoard = useMutation({
    mutationFn: () => fetcher(`/api/boards/${boardId}/duplicate`, { method: "POST" }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["project"] })
      toast("Board duplicated", "success")
      onClose()
      if (data?.id && board?.projectId) {
        navigate({ to: "/workspaces/$workspaceId/projects/$projectId/boards/$boardId", params: { workspaceId, projectId: board.projectId, boardId: data.id } })
      }
    },
    onError: (err: any) => toast(err.message || "Failed to duplicate board", "error"),
  })

  return (
    <Dialog open={open} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          updateBoard.mutate({ name: editName, description: editDescription, color: editColor || null })
        }}
        className="space-y-4"
      >
        <DialogTitle>Board settings</DialogTitle>
        <div>
          <Label>Name</Label>
          <Input value={editName} onChange={(e) => setEditName(e.target.value)} required />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
        </div>
        <div>
          <Label>Board background</Label>
          <ColorPicker plan={getPlan(workspace?.plan)} value={editColor} onChange={(c) => setEditColor(c || "")} />
          {getPlan(workspace?.plan) !== "free" && (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Pro gradients</p>
              <div className="flex flex-wrap gap-2">
                {GRADIENTS.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => {
                      const selected = editColor === g ? "" : g
                      setEditColor(selected)
                      const parsed = selected && parseLinearGradient(selected)
                      if (parsed) {
                        setGradientFrom(parsed.from)
                        setGradientTo(parsed.to)
                        setGradientAngle(parsed.angle)
                      }
                    }}
                    className={`h-8 w-14 rounded-md border-2 transition-transform hover:scale-105 ${editColor === g ? "border-foreground" : "border-transparent"}`}
                    style={{ background: g }}
                    title="Gradient"
                    aria-label="Select gradient"
                  />
                ))}
              </div>
              <div className="space-y-2 rounded-md border border-border bg-background p-2">
                <p className="text-xs font-medium text-muted-foreground">Custom gradient</p>
                <div
                  className="h-10 w-full rounded-md border border-border"
                  style={{ background: buildLinearGradient(gradientAngle, gradientFrom, gradientTo) }}
                />
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">Color 1</span>
                    <ColorPicker plan={getPlan(workspace?.plan)} value={gradientFrom} onChange={(c) => { const color = c || "#667eea"; setGradientFrom(color); setEditColor(buildLinearGradient(gradientAngle, color, gradientTo)) }} />
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">Color 2</span>
                    <ColorPicker plan={getPlan(workspace?.plan)} value={gradientTo} onChange={(c) => { const color = c || "#764ba2"; setGradientTo(color); setEditColor(buildLinearGradient(gradientAngle, gradientFrom, color)) }} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Angle</span>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={gradientAngle}
                    onChange={(e) => { const angle = Number(e.target.value); setGradientAngle(angle); setEditColor(buildLinearGradient(angle, gradientFrom, gradientTo)) }}
                    className="flex-1"
                  />
                  <span className="w-8 text-right text-xs text-muted-foreground">{gradientAngle}°</span>
                </div>
              </div>
            </div>
          )}
          {editColor && (
            <button type="button" onClick={() => setEditColor("")} className="mt-2 text-xs text-muted-foreground hover:text-foreground">
              Reset to default
            </button>
          )}
        </div>
        <div className="flex justify-between gap-2">
          <div className="flex gap-2">
            <Button
              variant="destructive"
              type="button"
              onClick={() => confirm("Delete this board? This cannot be undone.") && deleteBoard.mutate()}
              isLoading={deleteBoard.isPending}
            >
              Delete board
            </Button>
            <Button
              variant="outline"
              type="button"
              onClick={() => duplicateBoard.mutate()}
              isLoading={duplicateBoard.isPending}
            >
              <Copy className="mr-1.5 h-4 w-4" /> Duplicate
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} type="button">Cancel</Button>
            <Button type="submit" isLoading={updateBoard.isPending}>Save</Button>
          </div>
        </div>
      </form>
    </Dialog>
  )
}
